import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Linkedin, X, Mail, ArrowUp } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const Footer: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleQuickLinkClick = (section: string) => {
    if (section === 'Blog') {
      // Navigate to blog page
      navigate('/blog');
    } else if (location.pathname === '/') {
      // On homepage, scroll to section
      const element = document.querySelector(`#${section.toLowerCase()}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // On other pages, navigate to homepage with hash
      navigate(`/#${section.toLowerCase()}`);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className={`py-12 px-4 border-t ${
      isDark ? 'border-white/10' : 'border-gray-200/50'
    }`}>
      <div className="max-w-7xl mx-auto">
        {/* Back to Top Button */}
        <div className="flex justify-center mb-8">
          <button
            onClick={scrollToTop}
            className={`p-4 rounded-full glass border transition-all duration-300 hover:scale-110 ${
              isDark 
                ? 'glass-dark text-white hover:bg-white/10 border-white/20' 
                : 'glass-light text-charcoal hover:bg-white/60 border-gray-300/50'
            }`}
            aria-label="Back to top"
          >
            <ArrowUp size={20} />
          </button>
        </div>

        {/* Main Footer Content */}
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {/* Brand Section */}
          <div className="text-center md:text-left">
            <div className="mb-4">
              <span className={`text-3xl font-bold gradient-text`}>
                Kennedy Mmari
              </span>
            </div>
            <p className={`text-sm leading-relaxed max-w-sm mx-auto md:mx-0 ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Visionary CEO transforming industries through innovation, 
              strategic leadership, and exceptional results.
            </p>
          </div>

          {/* Quick Links */}
          <div className="text-center">
            <h4 className={`text-lg font-medium mb-4 ${
              isDark ? 'text-white' : 'text-charcoal'
            }`}>
              Quick Links
            </h4>
            <div className="space-y-2">
              {['About', 'Achievements', 'Experience', 'Blog', 'Contact'].map((link) => (
                <button
                  key={link}
                  onClick={() => handleQuickLinkClick(link)}
                  className={`block text-sm transition-colors duration-300 hover:text-sunrise-orange cursor-pointer ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  {link}
                </button>
              ))}
            </div>
          </div>

          {/* Social & Contact */}
          <div className="text-center md:text-right">
            <h4 className={`text-lg font-medium mb-4 ${
              isDark ? 'text-white' : 'text-charcoal'
            }`}>
              Connect With Me
            </h4>
            <div className="flex justify-center md:justify-end gap-4 mb-4">
              <a 
                href="#"
                className={`p-3 rounded-full glass border transition-all duration-300 hover:scale-110 ${
                  isDark 
                    ? 'glass-dark text-white hover:bg-white/10 border-white/20' 
                    : 'glass-light text-charcoal hover:bg-white/60 border-gray-300/50'
                }`}
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a 
                href="#"
                className={`p-3 rounded-full glass border transition-all duration-300 hover:scale-110 ${
                  isDark 
                    ? 'glass-dark text-white hover:bg-white/10 border-white/20' 
                    : 'glass-light text-charcoal hover:bg-white/60 border-gray-300/50'
                }`}
                aria-label="Twitter"
              >
                <X size={20} />
              </a>
              <a 
                href="mailto:kennedy@serengetibytes.com"
                className={`p-3 rounded-full glass border transition-all duration-300 hover:scale-110 ${
                  isDark 
                    ? 'glass-dark text-white hover:bg-white/10 border-white/20' 
                    : 'glass-light text-charcoal hover:bg-white/60 border-gray-300/50'
                }`}
                aria-label="Email"
              >
                <Mail size={20} />
              </a>
            </div>
            <div className={`text-sm ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <p>kennedy@serengetibytes.com</p>
              <p>+255 755 716 445</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`pt-8 border-t text-center ${
          isDark ? 'border-white/10' : 'border-gray-200/50'
        }`}>
          <p className={`text-sm ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            © {currentYear} Kennedy Mmari. All rights reserved. | Built with vision and precision.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
